import { Helmet } from "react-helmet";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import heroImage from "@/assets/blog/ai-answering-service.jpg";
import BlogArticleLayout from "@/components/BlogArticleLayout";

const WhatIsAIAnsweringService = () => {
  const { t } = useTranslation();

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "What is an AI answering service?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "An AI answering service uses artificial intelligence and natural language processing to handle phone calls automatically 24/7. It can answer questions, book appointments, capture leads, and route calls — without human intervention."
        }
      },
      {
        "@type": "Question",
        name: "How much does an AI answering service cost?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "AI answering services typically cost $49–199/month compared to $35,000+/year for a human receptionist. Most platforms like Call24x7.AI offer free trials and pay-as-you-go pricing."
        }
      },
      {
        "@type": "Question",
        name: "Can AI answering services handle complex calls?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Modern AI answering services handle routine calls excellently (FAQs, scheduling, lead capture). For complex issues, they seamlessly transfer to human agents with full context, creating a hybrid approach."
        }
      },
      {
        "@type": "Question",
        name: "How long does it take to set up an AI answering service?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "With platforms like Call24x7.AI, you can set up an AI answering service in under 60 seconds. Simply enter your website URL or describe your business, and the AI builds a custom phone agent automatically."
        }
      },
      {
        "@type": "Question",
        name: "Is an AI answering service suitable for small businesses?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes — AI answering services are especially valuable for small businesses that can't afford 24/7 staff. They ensure every call is answered, leads are captured, and customers get instant service at a fraction of the cost."
        }
      }
    ]
  };

  return (
    <>
      <Helmet>
        <title>{t('blog.whatIsAIAnsweringService.metaTitle')}</title>
        <meta name="description" content={t('blog.whatIsAIAnsweringService.metaDescription')} />
        <meta name="keywords" content="what is AI answering service, AI answering service cost, AI phone answering, automated answering service, AI receptionist, 24/7 answering service, virtual receptionist AI" />
        <link rel="canonical" href="https://call24x7.ai/blog/what-is-ai-answering-service" />
        <meta name="robots" content="index, follow" />
        <meta name="author" content="Call24x7 Team" />

        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://call24x7.ai/blog/what-is-ai-answering-service" />
        <meta property="og:title" content={t('blog.whatIsAIAnsweringService.title')} />
        <meta property="og:description" content={t('blog.whatIsAIAnsweringService.metaDescription')} />
        <meta property="og:site_name" content="Call24x7.AI" />
        <meta property="article:published_time" content="2025-12-09" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: t('blog.whatIsAIAnsweringService.title'),
            description: t('blog.whatIsAIAnsweringService.metaDescription'),
            author: { "@type": "Organization", name: "Call24x7.AI" },
            publisher: { "@type": "Organization", name: "Call24x7.AI" },
            datePublished: "2025-12-09",
            dateModified: "2026-03-24",
            mainEntityOfPage: "https://call24x7.ai/blog/what-is-ai-answering-service"
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <BlogArticleLayout
        title={t('blog.whatIsAIAnsweringService.title')}
        category={t('blog.categories.aiBasics')}
        date="December 9, 2025"
        readTime={`8 ${t('blog.minRead')}`}
        heroImage={heroImage}
        heroAlt="AI answering service technology — how it works"
      >
        <p className="text-xl text-muted-foreground mb-6">
          {t('blog.whatIsAIAnsweringService.intro')}
        </p>

        <Card className="bg-muted/50 border-primary/20 my-6">
          <CardContent className="pt-6">
            <p className="font-semibold mb-2">📊 Quick Stats:</p>
            <ul className="space-y-1 text-sm">
              <li>• AI answering services cost <strong>$49–199/mo</strong> vs <strong>$35K+/yr</strong> for a human receptionist</li>
              <li>• Handle <strong>unlimited simultaneous calls</strong> — no hold times</li>
              <li>• Set up in <strong>under 60 seconds</strong> with no coding required</li>
              <li>• Reduce missed calls by <strong>up to 95%</strong></li>
            </ul>
          </CardContent>
        </Card>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.whatIsAIAnsweringService.howItWorks')}</h2>
        <p>{t('blog.whatIsAIAnsweringService.howItWorksDesc')}</p>
        
        <p className="mt-4">{t('blog.whatIsAIAnsweringService.threeStages')}</p>
        
        <div className="space-y-3 my-6">
          {[t('blog.whatIsAIAnsweringService.stage1'), t('blog.whatIsAIAnsweringService.stage2'), t('blog.whatIsAIAnsweringService.stage3')].map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
              <span>{item}</span>
            </div>
          ))}
        </div>

        <h2 className="text-2xl font-bold mt-8 mb-4">AI Answering Service vs Traditional Options</h2>
        <div className="overflow-x-auto my-6">
          <table className="w-full border-collapse border border-border">
            <thead className="bg-muted">
              <tr>
                <th className="text-left py-3 px-4 border border-border">Feature</th>
                <th className="text-left py-3 px-4 border border-border">AI Answering Service</th>
                <th className="text-left py-3 px-4 border border-border">Human Receptionist</th>
                <th className="text-left py-3 px-4 border border-border">Voicemail</th>
              </tr>
            </thead>
            <tbody>
              <tr><td className="py-2 px-4 border border-border font-medium">Cost</td><td className="py-2 px-4 border border-border text-primary font-semibold">$49–199/mo</td><td className="py-2 px-4 border border-border">$35K+/yr</td><td className="py-2 px-4 border border-border">Free</td></tr>
              <tr><td className="py-2 px-4 border border-border font-medium">Availability</td><td className="py-2 px-4 border border-border text-primary font-semibold">24/7/365</td><td className="py-2 px-4 border border-border">Business hours</td><td className="py-2 px-4 border border-border">24/7 (no interaction)</td></tr>
              <tr><td className="py-2 px-4 border border-border font-medium">Simultaneous calls</td><td className="py-2 px-4 border border-border text-primary font-semibold">Unlimited</td><td className="py-2 px-4 border border-border">1 at a time</td><td className="py-2 px-4 border border-border">1 at a time</td></tr>
              <tr><td className="py-2 px-4 border border-border font-medium">Books appointments</td><td className="py-2 px-4 border border-border text-primary font-semibold">✓ Automatically</td><td className="py-2 px-4 border border-border">✓ Manually</td><td className="py-2 px-4 border border-border">✗</td></tr>
              <tr><td className="py-2 px-4 border border-border font-medium">Lead capture</td><td className="py-2 px-4 border border-border text-primary font-semibold">✓ Instant</td><td className="py-2 px-4 border border-border">✓</td><td className="py-2 px-4 border border-border">✗</td></tr>
              <tr><td className="py-2 px-4 border border-border font-medium">Setup time</td><td className="py-2 px-4 border border-border text-primary font-semibold">60 seconds</td><td className="py-2 px-4 border border-border">Weeks</td><td className="py-2 px-4 border border-border">Minutes</td></tr>
            </tbody>
          </table>
        </div>
        <p className="text-sm text-muted-foreground">
          For a detailed cost breakdown, see our <Link to="/blog/ai-vs-human-agents-cost-comparison" className="text-primary underline hover:no-underline">AI vs Human Agents cost comparison</Link>.
        </p>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.whatIsAIAnsweringService.benefits')}</h2>
        
        <h3 className="text-xl font-semibold mt-6 mb-3">1. {t('blog.whatIsAIAnsweringService.benefit1Title')}</h3>
        <p>{t('blog.whatIsAIAnsweringService.benefit1Desc')}</p>

        <h3 className="text-xl font-semibold mt-6 mb-3">2. {t('blog.whatIsAIAnsweringService.benefit2Title')}</h3>
        <p>{t('blog.whatIsAIAnsweringService.benefit2Desc')}</p>

        <h3 className="text-xl font-semibold mt-6 mb-3">3. {t('blog.whatIsAIAnsweringService.benefit3Title')}</h3>
        <p>{t('blog.whatIsAIAnsweringService.benefit3Desc')}</p>

        <h3 className="text-xl font-semibold mt-6 mb-3">4. {t('blog.whatIsAIAnsweringService.benefit4Title')}</h3>
        <p>{t('blog.whatIsAIAnsweringService.benefit4Desc')}</p>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.whatIsAIAnsweringService.useCases')}</h2>
        <p className="mb-4">AI answering services work across virtually every industry. Here are the most popular applications:</p>
        <ul className="list-disc list-inside space-y-2 my-4">
          <li>{t('blog.whatIsAIAnsweringService.useCase1')}</li>
          <li>{t('blog.whatIsAIAnsweringService.useCase2')}</li>
          <li>{t('blog.whatIsAIAnsweringService.useCase3')}</li>
          <li>{t('blog.whatIsAIAnsweringService.useCase4')}</li>
          <li>{t('blog.whatIsAIAnsweringService.useCase5')}</li>
        </ul>

        <Card className="bg-muted/50 border-border my-6">
          <CardContent className="pt-6">
            <p className="font-semibold mb-2">🏢 Industry-specific guides:</p>
            <ul className="space-y-1 text-sm">
              <li>• <Link to="/industries/medical-clinics" className="text-primary underline hover:no-underline">AI for Medical Clinics</Link> — HIPAA-ready reception & scheduling</li>
              <li>• <Link to="/industries/real-estate" className="text-primary underline hover:no-underline">AI for Real Estate</Link> — 24/7 lead capture & showing scheduling</li>
              <li>• <Link to="/industries/restaurants" className="text-primary underline hover:no-underline">AI for Restaurants</Link> — Reservations & takeout orders on autopilot</li>
              <li>• <Link to="/industries" className="text-primary underline hover:no-underline">View all 60+ industries →</Link></li>
            </ul>
          </CardContent>
        </Card>

        <h2 className="text-2xl font-bold mt-8 mb-4">How to Choose the Right AI Answering Service</h2>
        <p className="mb-4">Not all AI answering services are created equal. Here is what to look for:</p>
        <ol className="list-decimal list-inside space-y-3 my-4">
          <li><strong>Ease of setup:</strong> Can you get started in minutes, not weeks?</li>
          <li><strong>Voice quality:</strong> Does it sound natural or robotic?</li>
          <li><strong>Integration support:</strong> Does it connect to your CRM, calendar, and tools?</li>
          <li><strong>Multilingual:</strong> Can it handle calls in multiple languages?</li>
          <li><strong>Pricing transparency:</strong> Are there hidden per-minute fees?</li>
        </ol>
        <p className="text-sm text-muted-foreground">
          See our full <Link to="/blog/best-ai-phone-agent-software" className="text-primary underline hover:no-underline">comparison of 7 best AI phone agent platforms</Link> for detailed reviews.
        </p>

        <h2 className="text-2xl font-bold mt-8 mb-4">Frequently Asked Questions</h2>
        <div className="space-y-6 my-6">
          <div>
            <h3 className="font-semibold mb-2">What is an AI answering service?</h3>
            <p className="text-muted-foreground">An AI answering service uses artificial intelligence and natural language processing to handle phone calls automatically 24/7. It can answer questions, book appointments, capture leads, and route calls — without human intervention.</p>
          </div>
          <div>
            <h3 className="font-semibold mb-2">How much does an AI answering service cost?</h3>
            <p className="text-muted-foreground">AI answering services typically cost $49–199/month compared to $35,000+/year for a human receptionist. Most platforms like Call24x7.AI offer free trials and pay-as-you-go pricing.</p>
          </div>
          <div>
            <h3 className="font-semibold mb-2">Can AI answering services handle complex calls?</h3>
            <p className="text-muted-foreground">Modern AI answering services handle routine calls excellently (FAQs, scheduling, lead capture). For complex issues, they seamlessly transfer to human agents with full context.</p>
          </div>
          <div>
            <h3 className="font-semibold mb-2">How long does it take to set up?</h3>
            <p className="text-muted-foreground">With Call24x7.AI, setup takes under 60 seconds. Enter your website URL or describe your business, and the AI builds a custom phone agent automatically.</p>
          </div>
          <div>
            <h3 className="font-semibold mb-2">Is it suitable for small businesses?</h3>
            <p className="text-muted-foreground">Absolutely — AI answering services are especially valuable for small businesses that cannot afford 24/7 staff. See our <Link to="/blog/ai-receptionist-for-small-business" className="text-primary underline hover:no-underline">AI receptionist guide for small businesses</Link>.</p>
          </div>
        </div>

        <h2 className="text-2xl font-bold mt-8 mb-4">{t('blog.whatIsAIAnsweringService.gettingStarted')}</h2>
        <p>{t('blog.whatIsAIAnsweringService.gettingStartedDesc')}</p>

        <Card className="bg-gradient-to-r from-primary/10 to-secondary/10 border-primary/20 mt-8">
          <CardContent className="pt-6 text-center">
            <h3 className="text-xl font-bold mb-2">{t('blog.whatIsAIAnsweringService.ctaTitle')}</h3>
            <p className="text-muted-foreground mb-4">{t('blog.whatIsAIAnsweringService.ctaDesc')}</p>
            <Button onClick={() => window.location.href = "https://manage.call24x7.ai/signup"}>
              {t('blog.whatIsAIAnsweringService.ctaButton')}
            </Button>
          </CardContent>
        </Card>
      </BlogArticleLayout>
    </>
  );
};

export default WhatIsAIAnsweringService;
